import React, { useState, useEffect, useRef, useMemo } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import * as api from "../api";
import { C, R, F, numeric } from "../theme";
import { Icon } from "../icons";
import { Card } from "./UI";

// ─── Pastilles de carte — SVG inline (aucun emoji) ────────────────────────────
// Le tracé reprend les icônes lucide « car » et « map-pin » utilisées par <Icon>.
const pinShell = (bg, border, svg) => `
  <div style="width:30px;height:30px;border-radius:50%;background:${bg};
              border:1px solid ${border};box-shadow:0 1px 3px rgba(10,37,64,.25);
              display:flex;align-items:center;justify-content:center;color:#fff">
    ${svg}
  </div>`;

const CAR_SVG = `
  <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 24 24"
       fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
    <path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9A3.7 3.7 0 0 0 2 12v4c0 .6.4 1 1 1h2"/>
    <circle cx="7" cy="17" r="2"/><path d="M9 17h6"/><circle cx="17" cy="17" r="2"/>
  </svg>`;

const PIN_SVG = `
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
       fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
    <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0"/><circle cx="12" cy="10" r="3"/>
  </svg>`;

export const COLLECTOR_ICON = L.divIcon({
  html: pinShell(C.navy, C.navy, CAR_SVG),
  className: '', iconSize: [36, 36], iconAnchor: [18, 18],
});
export const CLIENT_ICON = L.divIcon({
  html: pinShell(C.amber, C.amber, PIN_SVG),
  className: '', iconSize: [32, 32], iconAnchor: [16, 32],
});

export function MapBoundsUpdater({ positions }) {
  const map = useMap();
  useEffect(() => {
    if (positions.length >= 2) {
      const bounds = L.latLngBounds(positions.map(p => [p.lat, p.lng]));
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 16 });
    } else if (positions.length === 1) {
      map.setView([positions[0].lat, positions[0].lng], 15);
    }
  }, [positions, map]);
  return null;
}

export function DraggableMarker({ lat, lng, onMove }) {
  const markerRef = useRef(null);
  const map = useMap();

  useEffect(() => {
    map.setView([lat, lng], map.getZoom());
  }, [lat, lng, map]);

  const eventHandlers = useMemo(() => ({
    dragend() {
      const marker = markerRef.current;
      if (marker) {
        const pos = marker.getLatLng();
        onMove(pos.lat, pos.lng);
      }
    },
  }), [onMove]);

  return <Marker draggable position={[lat, lng]} ref={markerRef} eventHandlers={eventHandlers} />;
}

export function CollectorMap({ orderId }) {
  const [tracking, setTracking] = useState(false);
  const [collectorPos, setCollectorPos] = useState(null);
  const [clientPos, setClientPos] = useState(null);
  const [collectorName, setCollectorName] = useState("");
  const [departedAt, setDepartedAt] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);

  useEffect(() => {
    let interval;
    async function poll() {
      try {
        const data = await api.getCollectorLocation(orderId);
        if (data.tracking) {
          setTracking(true);
          setCollectorPos({ lat: data.latitude, lng: data.longitude });
          setCollectorName(data.collector_name || "Collecteur");
          setDepartedAt(data.departed_at);
          setLastUpdate(new Date(data.updated_at));
          if (data.client_lat && data.client_lng) {
            setClientPos({ lat: data.client_lat, lng: data.client_lng });
          }
        } else {
          setTracking(false);
        }
      } catch { setTracking(false); }
    }
    poll();
    interval = setInterval(poll, 8000);
    return () => clearInterval(interval);
  }, [orderId]);

  if (!tracking) return null;

  const positions = [collectorPos, clientPos].filter(Boolean);
  const defaultCenter = collectorPos || { lat: 14.6937, lng: -17.4441 };

  const timeSinceDeparture = departedAt
    ? Math.round((Date.now() - new Date(departedAt).getTime()) / 60000)
    : null;

  return (
    <Card style={{ marginBottom: 20, padding: 0, overflow: "hidden" }}>
      {/* ─── En-tête ─────────────────────────────────────────────────────── */}
      <div style={{
        padding: "12px 14px",
        borderBottom: `1px solid ${C.border}`,
        background: C.surfaceAlt,
        display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 9, minWidth: 0 }}>
          <span style={{
            width: 30, height: 30, borderRadius: R.md, flexShrink: 0,
            background: C.navy, color: C.inkOn,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <Icon name="collect" size={16} color={C.inkOn} />
          </span>
          <div style={{ minWidth: 0 }}>
            <div className="truncate" style={{ fontSize: F.size.md, fontWeight: F.weight.semi, color: C.ink }}>
              {collectorName} est en route
            </div>
            {timeSinceDeparture !== null && (
              <div style={{ fontSize: F.size.sm, color: C.inkMuted, marginTop: 1, ...numeric }}>
                Parti il y a {timeSinceDeparture} min
              </div>
            )}
          </div>
        </div>

        <span style={{
          display: "inline-flex", alignItems: "center", gap: 6, flexShrink: 0,
          background: C.successSoft, border: `1px solid ${C.successBorder}`, color: C.success,
          borderRadius: R.pill, padding: "2px 9px",
          fontSize: F.size.xs, fontWeight: F.weight.semi,
          letterSpacing: ".04em", textTransform: "uppercase",
        }}>
          <span style={{
            width: 5, height: 5, borderRadius: "50%", background: C.success,
            animation: "pulse 1.6s ease infinite",
          }} />
          En direct
        </span>
      </div>

      <div style={{ height: 280 }}>
        <MapContainer
          center={[defaultCenter.lat, defaultCenter.lng]}
          zoom={14}
          style={{ height: "100%", width: "100%" }}
          zoomControl={false}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {collectorPos && (
            <Marker position={[collectorPos.lat, collectorPos.lng]} icon={COLLECTOR_ICON}>
              <Popup>{collectorName}</Popup>
            </Marker>
          )}
          {clientPos && (
            <Marker position={[clientPos.lat, clientPos.lng]} icon={CLIENT_ICON}>
              <Popup>Votre position</Popup>
            </Marker>
          )}
          <MapBoundsUpdater positions={positions} />
        </MapContainer>
      </div>

      {/* ─── Légende ─────────────────────────────────────────────────────── */}
      <div style={{
        padding: "9px 14px", borderTop: `1px solid ${C.border}`, background: C.surfaceAlt,
        display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12,
        fontSize: F.size.sm, color: C.inkMuted, flexWrap: "wrap",
      }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <Icon name="refresh" size={13} />
          Position mise à jour toutes les 8 s
        </span>
        {lastUpdate && (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6, ...numeric }}>
            <Icon name="clock" size={13} />
            {lastUpdate.toLocaleTimeString("fr-FR")}
          </span>
        )}
      </div>
    </Card>
  );
}
